if (!customElements.get('m-faq')) {
  class mFaq extends HTMLElement {
    constructor() {
      super();
      mFaq.instanceCounter = (mFaq.instanceCounter || 0) + 1;
      this.instanceId = mFaq.instanceCounter;
      this.header = null;
      this.headerResizeObserver = null;
      this.updateHeaderHeight = this.updateHeaderHeight.bind(this);
      this.onAccordionClick = this.onAccordionClick.bind(this);
      this.updateAccordionHeights = this.updateAccordionHeights.bind(this);
      this.onNavClick = this.onNavClick.bind(this);
      this.onWindowScroll = this.onWindowScroll.bind(this);
      this.updateActiveNavOnScroll = this.updateActiveNavOnScroll.bind(this);
      this.scrollAnimationFrame = null;
    }

    connectedCallback() {
      this.header = this.getHeader();
      this.updateHeaderHeight();
      this.bindHeaderHeightEvents();
      this.initAccordions();
      this.initCategoriesNav();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.updateHeaderHeight);
      window.removeEventListener('resize', this.updateAccordionHeights);
      window.removeEventListener('scroll', this.onWindowScroll);
      document.removeEventListener('theme:resize', this.updateHeaderHeight);
      document.removeEventListener('theme:resize', this.updateAccordionHeights);
      this.removeEventListener('click', this.onAccordionClick);
      this.removeEventListener('click', this.onNavClick);

      if (this.scrollAnimationFrame) {
        window.cancelAnimationFrame(this.scrollAnimationFrame);
      }

      if (this.headerResizeObserver) {
        this.headerResizeObserver.disconnect();
      }
    }

    getHeader() {
      return document.querySelector('[data-header-height]') || document.querySelector('.header-sections');
    }

    bindHeaderHeightEvents() {
      window.addEventListener('resize', this.updateHeaderHeight);
      document.addEventListener('theme:resize', this.updateHeaderHeight);

      if ('ResizeObserver' in window && this.header) {
        this.headerResizeObserver = new ResizeObserver(this.updateHeaderHeight);
        this.headerResizeObserver.observe(this.header);
      }
    }

    updateHeaderHeight() {
      this.header = this.header || this.getHeader();
      const headerHeight = this.getHeaderHeight();

      this.style.setProperty('--faq-header-height', `${headerHeight}px`);
    }

    getHeaderHeight() {
      this.header = this.header || this.getHeader();

      return this.header ? Math.ceil(this.header.getBoundingClientRect().height) : 0;
    }

    getScrollOffset() {
      return this.getHeaderHeight() + 32;
    }

    initCategoriesNav() {
      this.navButtons = Array.from(this.querySelectorAll('[data-categories-tab]'));
      this.scrollTargets = Array.from(this.querySelectorAll('[data-categories-scroll]'));

      if (!this.navButtons.length || !this.scrollTargets.length) return;

      this.addEventListener('click', this.onNavClick);
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      this.setActiveNavButton(this.navButtons[0]);
      this.updateActiveNavOnScroll();
    }

    onNavClick(event) {
      const button = event.target.closest('[data-categories-tab]');

      if (!button || !this.contains(button)) return;

      const targetId = button.dataset.categoriesTab;
      const target = this.scrollTargets.find((item) => item.dataset.categoriesScroll === targetId);

      if (!target) return;

      this.setActiveNavButton(button);
      this.scrollToTarget(target);
    }

    setActiveNavButton(activeButton) {
      this.navButtons.forEach((button) => {
        const isActive = button === activeButton;

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    scrollToTarget(target) {
      this.updateHeaderHeight();

      const targetTop = target.getBoundingClientRect().top + window.scrollY;
      const scrollTop = targetTop - this.getScrollOffset();

      window.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });
    }

    onWindowScroll() {
      if (this.scrollAnimationFrame) return;

      this.scrollAnimationFrame = window.requestAnimationFrame(() => {
        this.updateActiveNavOnScroll();
        this.scrollAnimationFrame = null;
      });
    }

    updateActiveNavOnScroll() {
      const currentTarget = this.getCurrentScrollTarget();

      if (!currentTarget) return;

      const activeButton = this.navButtons.find((button) => button.dataset.categoriesTab === currentTarget.dataset.categoriesScroll);

      if (activeButton) {
        this.setActiveNavButton(activeButton);
      }
    }

    getCurrentScrollTarget() {
      const scrollLine = window.scrollY + this.getScrollOffset();

      return this.scrollTargets.reduce((currentTarget, target) => {
        const targetTop = target.getBoundingClientRect().top + window.scrollY;

        if (targetTop <= scrollLine) {
          return target;
        }

        return currentTarget;
      }, this.scrollTargets[0]);
    }

    initAccordions() {
      this.accordions = Array.from(this.querySelectorAll('.faq__content-accordion'));

      this.accordions.forEach((accordion, index) => {
        const button = accordion.querySelector('.faq__accordion-title');
        const content = accordion.querySelector('.faq__accordion-content');

        if (!button || !content) return;

        const contentId = content.id || `faq-accordion-content-${this.instanceId}-${index}`;

        content.id = contentId;
        button.setAttribute('aria-controls', contentId);
        button.setAttribute('aria-expanded', 'false');
        accordion.classList.remove('is-open');
        this.setAccordionHeight(accordion);
      });

      this.addEventListener('click', this.onAccordionClick);
      window.addEventListener('resize', this.updateAccordionHeights);
      document.addEventListener('theme:resize', this.updateAccordionHeights);
    }

    onAccordionClick(event) {
      const button = event.target.closest('.faq__accordion-title');

      if (!button || !this.contains(button)) return;

      const accordion = button.closest('.faq__content-accordion');
      const isOpen = accordion.classList.contains('is-open');

      this.closeAllAccordions();

      if (!isOpen) {
        this.openAccordion(accordion);
      }
    }

    openAccordion(accordion) {
      const button = accordion.querySelector('.faq__accordion-title');

      this.setAccordionHeight(accordion);
      accordion.classList.add('is-open');
      button?.setAttribute('aria-expanded', 'true');
    }

    closeAccordion(accordion) {
      const button = accordion.querySelector('.faq__accordion-title');

      accordion.classList.remove('is-open');
      button?.setAttribute('aria-expanded', 'false');
    }

    closeAllAccordions() {
      this.accordions.forEach((accordion) => this.closeAccordion(accordion));
    }

    setAccordionHeight(accordion) {
      const text = accordion.querySelector('.faq__accordion-text');
      const contentHeight = text ? Math.ceil(text.scrollHeight) : 0;

      accordion.style.setProperty('--faq-accordion-content-height', `${contentHeight}px`);
    }

    updateAccordionHeights() {
      this.accordions.forEach((accordion) => this.setAccordionHeight(accordion));
    }
  }

  customElements.define('m-faq', mFaq);
}
